import type { FC} from 'react';
import { useEffect } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { Box, Container, useTheme } from '@mui/material';

import { useUser } from '@/hooks/v2/use-user';
import { Seo } from '@/components/seo';

import { AnnouncementsTable } from './components/announcements-table';
import { CommunityPageHeader } from '@/components/common/community-page-header';
import { Megaphone as MegaphoneIcon } from '@phosphor-icons/react';
import { getAnnouncements } from '@/api/announcements';
import { useQuery } from '@tanstack/react-query';

const AnnouncementsPage: FC = () => {
  const { user, community } = useUser();
  const theme = useTheme();
  const { data: announcements, isLoading, error } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => getAnnouncements(),
  });

  // Initialize amplitude once when the component mounts.
  useEffect(() => {
    amplitude.init('80c3eab858c5754cd3dd9861540e123d', { autocapture: true });
  }, []);

  // Update amplitude's userId whenever the user changes.
  useEffect(() => {
    if (user?.id) {
      amplitude.setUserId(user.id);
      amplitude.track('Announcements');
    }
  }, [user]);

  return (
    <>
      <Seo title="Announcements" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="xl">
          <CommunityPageHeader
            icon={<MegaphoneIcon size={36} />}
            subtitle="Manage and view community announcements, notifications, and important updates"
            title={'Announcements'}
          />

          {/* Announcements Table */}
          <AnnouncementsTable 
            error={error ? new Error(error.message) : null} 
            loading={isLoading} 
            rows={announcements || []} 
          />
        </Container>
      </Box>
    </>
  );
};

export default AnnouncementsPage;
